import React, { useState } from 'react';

const UniversalRequestMonitor = () => {
  const [allRequests, setAllRequests] = useState([]);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [originalFetch, setOriginalFetch] = useState(null);
  const [originalXHR, setOriginalXHR] = useState(null);
  const [filterPattern, setFilterPattern] = useState('');
  const [showAllRequests, setShowAllRequests] = useState(false);

  const addRequest = (requestData) => {
    setAllRequests(prev => [...prev.slice(-50), {
      ...requestData,
      id: Date.now() + Math.random(),
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  const startUniversalMonitoring = () => {
    if (isMonitoring) return;

    console.log('🔍 Starting universal request monitoring...');
    
    // Store original fetch
    const originalFetchRef = window.fetch;
    setOriginalFetch(() => originalFetchRef);
    
    // Store original XMLHttpRequest
    const originalXHRRef = window.XMLHttpRequest;
    setOriginalXHR(() => originalXHRRef);
    
    // Monitor ALL fetch requests
    window.fetch = async function(...args) {
      const [url, options] = args;
      const urlString = typeof url === 'string' ? url : url.toString();
      
      // Log ALL requests (we'll filter in UI)
      const requestInfo = {
        type: 'fetch',
        url: urlString,
        method: options?.method || 'GET',
        headers: options?.headers || {},
        hasBody: !!options?.body,
        bodyPreview: options?.body ? 
          (typeof options.body === 'string' ? 
            options.body.substring(0, 300) + (options.body.length > 300 ? '...' : '') : 
            '[FormData or other body type]'
          ) : null,
        fullBody: options?.body && typeof options.body === 'string' ? options.body : null
      };

      try {
        const response = await originalFetchRef.apply(this, args);
        const responseClone = response.clone();
        
        // Try to read response content
        let responseText = '';
        let responseData = null;
        
        try {
          responseText = await responseClone.text();
          
          // Try to parse as JSON
          if (responseText && (responseText.startsWith('{') || responseText.startsWith('['))) {
            try {
              responseData = JSON.parse(responseText);
            } catch (e) {
              // Not JSON, that's fine
            }
          }
        } catch (readError) {
          responseText = `[Could not read response: ${readError.message}]`;
        }

        // Analyze for payment failure patterns
        const isPaymentFailure = analyzeForPaymentFailure(responseText, responseData, response);
        
        addRequest({
          ...requestInfo,
          status: response.status,
          statusText: response.statusText,
          responseHeaders: Object.fromEntries(response.headers.entries()),
          responseText: responseText.substring(0, 1000) + (responseText.length > 1000 ? '...' : ''),
          fullResponseText: responseText,
          responseData,
          isPaymentFailure,
          failureDetails: isPaymentFailure ? analyzeFailureDetails(responseText, responseData) : null
        });

        return response;
        
      } catch (error) {
        addRequest({
          ...requestInfo,
          error: error.message,
          isError: true
        });
        throw error;
      }
    };

    // Monitor ALL XMLHttpRequest
    window.XMLHttpRequest = function() {
      const xhr = new originalXHRRef();
      const originalOpen = xhr.open;
      const originalSend = xhr.send;
      
      let requestData = {};
      
      xhr.open = function(method, url, ...rest) {
        requestData = {
          type: 'xhr',
          method,
          url: url.toString(),
          timestamp: Date.now()
        };
        return originalOpen.apply(this, [method, url, ...rest]);
      };
      
      xhr.send = function(body) {
        requestData.hasBody = !!body;
        requestData.bodyPreview = body ? 
          (typeof body === 'string' ? 
            body.substring(0, 300) + (body.length > 300 ? '...' : '') : 
            '[FormData or other body type]'
          ) : null;
        requestData.fullBody = body && typeof body === 'string' ? body : null;
        
        const originalOnLoad = xhr.onload;
        const originalOnError = xhr.onerror;
        
        xhr.onload = function() {
          const responseText = xhr.responseText || '';
          let responseData = null;
          
          try {
            if (responseText && (responseText.startsWith('{') || responseText.startsWith('['))) {
              responseData = JSON.parse(responseText);
            }
          } catch (e) {
            // Not JSON
          }
          
          const isPaymentFailure = analyzeForPaymentFailure(responseText, responseData, xhr);
          
          addRequest({
            ...requestData,
            status: xhr.status,
            statusText: xhr.statusText,
            responseText: responseText.substring(0, 1000) + (responseText.length > 1000 ? '...' : ''),
            fullResponseText: responseText,
            responseData,
            isPaymentFailure,
            failureDetails: isPaymentFailure ? analyzeFailureDetails(responseText, responseData) : null
          });
          
          if (originalOnLoad) originalOnLoad.apply(this, arguments);
        };
        
        xhr.onerror = function() {
          addRequest({
            ...requestData,
            error: 'XHR Error',
            isError: true
          });
          if (originalOnError) originalOnError.apply(this, arguments);
        };
        
        return originalSend.apply(this, arguments);
      };
      
      return xhr;
    };
    
    setIsMonitoring(true);
    console.log('✅ Universal monitoring started - capturing ALL requests');
  };

  const stopMonitoring = () => {
    if (!isMonitoring) return;
    
    console.log('⏹️ Stopping universal monitoring...');
    
    if (originalFetch) {
      window.fetch = originalFetch;
    }
    
    if (originalXHR) {
      window.XMLHttpRequest = originalXHR;
    }
    
    setIsMonitoring(false);
    console.log('✅ Universal monitoring stopped');
  };

  const analyzeForPaymentFailure = (responseText, responseData, response) => {
    // Check status code
    if (response.status >= 400) return true;
    
    // Check JSON data first
    if (responseData) {
      if (responseData.success === false) return true;
      if (responseData.error) return true;
      if (responseData.status === 'failed' || responseData.status === 'error') return true;
      if (responseData.message && /fail|error|decline|invalid/i.test(responseData.message)) return true;
    }
    
    // Check response text
    const failurePatterns = [
      /transaction.*failed/i,
      /payment.*failed/i,
      /payment.*did.*not.*succeed/i,
      /card.*declined/i,
      /insufficient.*funds/i,
      /invalid.*card/i,
      /expired.*card/i,
      /cvv.*invalid/i,
      /authorization.*failed/i,
      /processing.*error/i,
      /"success":\s*false/i,
      /"error":\s*"[^"]+"/i
    ];
    
    return failurePatterns.some(pattern => pattern.test(responseText));
  };

  const analyzeFailureDetails = (responseText, responseData) => {
    const details = {
      reason: 'Unknown failure',
      source: 'unknown',
      errorCode: null,
      errorMessage: null
    };
    
    if (responseData) {
      if (responseData.message) {
        details.reason = responseData.message;
        details.source = 'json_message';
      }
      if (responseData.error) {
        details.errorMessage = responseData.error;
        details.source = 'json_error';
      }
      if (responseData.error_code) {
        details.errorCode = responseData.error_code;
      }
    }
    
    // Extract from text patterns
    const patterns = [
      { pattern: /Transaction failed\. Payment did not succeed/i, reason: 'Transaction failed - Payment did not succeed' },
      { pattern: /card.*declined/i, reason: 'Card declined by issuer' },
      { pattern: /insufficient.*funds/i, reason: 'Insufficient funds' },
      { pattern: /invalid.*card/i, reason: 'Invalid card information' }
    ];
    
    for (const { pattern, reason } of patterns) {
      if (pattern.test(responseText)) {
        details.reason = reason;
        details.source = 'text_pattern';
        break;
      }
    }
    
    return details;
  };

  const clearRequests = () => {
    setAllRequests([]);
  };

  const exportRequests = () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      totalRequests: allRequests.length,
      paymentFailures: allRequests.filter(r => r.isPaymentFailure),
      allRequests: allRequests
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `universal-requests-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filter requests based on pattern
  const filteredRequests = allRequests.filter(request => {
    if (!filterPattern) return showAllRequests || isPaymentRelated(request);
    return request.url.toLowerCase().includes(filterPattern.toLowerCase()) ||
           (request.responseText && request.responseText.toLowerCase().includes(filterPattern.toLowerCase()));
  });

  const isPaymentRelated = (request) => {
    const url = request.url.toLowerCase();
    return url.includes('payment') || 
           url.includes('checkout') || 
           url.includes('order') || 
           url.includes('place_order') ||
           request.isPaymentFailure;
  };

  const paymentFailures = allRequests.filter(r => r.isPaymentFailure);

  const getRequestStyle = (request) => {
    let backgroundColor = '#f8f9fa';
    let borderColor = '#dee2e6';
    
    if (request.isPaymentFailure) {
      backgroundColor = '#f8d7da';
      borderColor = '#dc3545';
    } else if (request.isError) {
      backgroundColor = '#f8d7da';
      borderColor = '#dc3545';
    } else if (request.status >= 400) {
      backgroundColor = '#fff3cd';
      borderColor = '#ffc107';
    } else if (isPaymentRelated(request)) {
      backgroundColor = '#d4edda';
      borderColor = '#28a745';
    }
    
    return {
      padding: '12px',
      margin: '8px 0',
      border: `2px solid ${borderColor}`,
      borderRadius: '8px',
      backgroundColor,
      fontSize: '14px'
    };
  };

  return (
    <div style={{ 
      backgroundColor: 'white', 
      padding: '24px', 
      borderRadius: '8px', 
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
      marginBottom: '24px' 
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px' 
      }}>
        <h2 style={{ margin: 0, color: '#333' }}>
          🌐 Universal Request Monitor
        </h2>
        <div style={{ 
          padding: '4px 12px', 
          backgroundColor: isMonitoring ? '#d4edda' : '#e9ecef', 
          borderRadius: '16px', 
          fontSize: '12px',
          color: isMonitoring ? '#155724' : '#6c757d'
        }}>
          {isMonitoring ? 'Monitoring' : 'Stopped'}
        </div>
      </div>

      {/* Status Summary */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '12px',
        marginBottom: '24px'
      }}>
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#d1ecf1', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{allRequests.length}</div>
          <div style={{ fontSize: '12px' }}>Total Requests</div>
        </div>
        
        <div style={{ 
          padding: '12px', 
          backgroundColor: paymentFailures.length > 0 ? '#f8d7da' : '#d4edda', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{paymentFailures.length}</div>
          <div style={{ fontSize: '12px' }}>Payment Failures</div>
        </div>
        
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#fff3cd', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
            {allRequests.filter(r => isPaymentRelated(r)).length}
          </div>
          <div style={{ fontSize: '12px' }}>Payment Related</div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              backgroundColor: isMonitoring ? '#dc3545' : '#28a745',
              color: 'white'
            }}
            onClick={isMonitoring ? stopMonitoring : startUniversalMonitoring}
          >
            {isMonitoring ? '⏹️ Stop Monitoring' : '▶️ Start Monitoring'}
          </button>
          
          <button
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              backgroundColor: '#6c757d',
              color: 'white'
            }}
            onClick={clearRequests}
          >
            🗑️ Clear
          </button>
          
          <button
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              backgroundColor: '#17a2b8',
              color: 'white'
            }}
            onClick={exportRequests}
          >
            💾 Export
          </button>
        </div>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Filter requests..."
            value={filterPattern}
            onChange={(e) => setFilterPattern(e.target.value)}
            style={{
              padding: '8px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '200px'
            }}
          />
          
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <input
              type="checkbox"
              checked={showAllRequests}
              onChange={(e) => setShowAllRequests(e.target.checked)}
            />
            Show all requests (not just payment-related)
          </label>
        </div>
      </div>

      {/* Payment Failures Summary */}
      {paymentFailures.length > 0 && (
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#f8d7da', 
          borderRadius: '8px',
          marginBottom: '24px',
          border: '2px solid #dc3545'
        }}>
          <h3 style={{ margin: '0 0 12px 0', color: '#721c24' }}>
            🚨 Payment Failures Detected ({paymentFailures.length})
          </h3>
          {paymentFailures.map((failure, index) => (
            <div key={failure.id} style={{ 
              marginBottom: '8px',
              padding: '8px',
              backgroundColor: 'rgba(255,255,255,0.5)',
              borderRadius: '4px'
            }}>
              <strong>{index + 1}. {failure.url.split('/').pop()}</strong>
              <div style={{ fontSize: '12px', color: '#721c24' }}>
                Status: {failure.status} | Reason: {failure.failureDetails?.reason || 'Unknown'}
              </div>
              {failure.failureDetails?.errorMessage && (
                <div style={{ fontSize: '12px', fontStyle: 'italic' }}>
                  Error: {failure.failureDetails.errorMessage}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Request List */}
      <div>
        <h3 style={{ marginBottom: '16px' }}>
          📝 Requests ({filteredRequests.length})
        </h3>
        
        <div style={{ 
          maxHeight: '400px',
          overflow: 'auto'
        }}>
          {filteredRequests.length === 0 ? (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#6c757d',
              border: '1px solid #dee2e6',
              borderRadius: '8px'
            }}>
              {isMonitoring ? 
                'No requests captured yet. Try making a payment...' : 
                'Click "Start Monitoring" and make a payment to see requests'
              }
            </div>
          ) : (
            filteredRequests.slice(-20).reverse().map((request) => (
              <div key={request.id} style={getRequestStyle(request)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div>
                    <strong>{request.method} {request.url.split('/').pop()}</strong>
                    {request.isPaymentFailure && <span style={{ color: '#dc3545', marginLeft: '8px' }}>🚨 FAILURE</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>
                    {request.timestamp}
                  </div>
                </div>
                
                <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                  <strong>URL:</strong> {request.url}
                </div>
                
                <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                  <strong>Status:</strong> {request.status} {request.statusText}
                </div>
                
                {request.bodyPreview && (
                  <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                    <strong>Request Body:</strong> {request.bodyPreview}
                  </div>
                )}
                
                {request.responseText && (
                  <details style={{ fontSize: '12px' }}>
                    <summary style={{ cursor: 'pointer', marginBottom: '4px' }}>
                      <strong>Response Content</strong>
                    </summary>
                    <pre style={{ 
                      backgroundColor: 'rgba(0,0,0,0.1)', 
                      padding: '8px', 
                      borderRadius: '4px',
                      overflow: 'auto',
                      fontSize: '11px'
                    }}>
                      {request.responseText}
                    </pre>
                  </details>
                )}
                
                {request.failureDetails && (
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '8px', 
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    borderRadius: '4px'
                  }}>
                    <strong>Failure Details:</strong>
                    <div style={{ fontSize: '12px' }}>
                      Reason: {request.failureDetails.reason}<br/>
                      Source: {request.failureDetails.source}
                      {request.failureDetails.errorCode && <><br/>Error Code: {request.failureDetails.errorCode}</>}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default UniversalRequestMonitor;